<template>
  <div>
    <div
      v-for="(item, idx) in banners"
      :key="`global-alert-${idx}-${item.kind}`"
      class="global-subscription-alert"
      :class="item.level === 'critical' ? 'is-critical' : 'is-warning'"
    >
      <div class="global-subscription-alert__content">
        <i class="fa" :class="item.level === 'critical' ? 'fa-exclamation-circle' : 'fa-clock-o'"></i>
        <div>
          <h6>{{ item.title }}</h6>
          <p>{{ item.message }}</p>
        </div>
      </div>

      <button v-if="item.kind === 'demo'" type="button" class="btn btn-primary btn-sm" @click="goToRfcSettings">
        Registrar RFC
      </button>

      <button v-if="item.kind === 'subscription' && showRenewCta && !efirmaIncident" type="button" class="btn btn-primary btn-sm" @click="goToSubscription">
        Renovar ahora
      </button>
    </div>
  </div>
</template>

<script>
import accountService from '@/services/account';
import dashboardService from '@/services/dashboard';

export default {
  name: 'GlobalSubscriptionAlert',
  demoRfc: 'XAXX010101000',
  data() {
    return {
      status: null,
      loading: false,
      efirmaIncident: null,
    };
  },
  computed: {
    isDataSectionRoute() {
      const routeName = String(this.$route?.name || '');

      return [
        'dashboard',
        'issuedCfdis',
        'receivedCfdis',
        'cfdisPorPagar',
        'cfdisPorCobrar',
        'paymentReconciliationIssued',
        'paymentReconciliationReceived',
        'diotWorkpaper',
        'downloadCenter',
        'taxesSummary',
        'validations',
      ].includes(routeName);
    },
    isInsideSubscriptionSettings() {
      return this.$route?.name === 'settings' && String(this.$route?.query?.section || '') === 'subscription';
    },
    showRenewCta() {
      return !this.isInsideSubscriptionSettings;
    },
    subscriptionBanner() {
      const status = this.status;
      if (!status && !this.efirmaIncident) return null;

      if (this.efirmaIncident) {
        return {
          level: 'critical',
          title: this.efirmaIncident?.warning_title || 'Se detectaron fallas continuas en descargas CFDI',
          message: this.efirmaIncident?.warning_message || 'Durante 7 días consecutivos han fallado las descargas CFDI. Revisa y actualiza la e.firma para reactivar las descargas.',
        };
      }

      if (status.warning) {
        return status.warning;
      }

      if (status.is_expired) {
        return {
          level: 'critical',
          title: 'Tu suscripción está vencida',
          message: 'No podrás registrar nuevas solicitudes de descarga ni RFCs hasta renovar tu paquete.',
        };
      }

      const days = Number(status.days_remaining ?? 9999);
      if (status.has_active_period && days <= 10) {
        return {
          level: 'warning',
          title: 'Tu suscripción está por vencer',
          message: `Vence en ${days} día(s). Renueva hoy para mantener tus descargas y automatizaciones activas.`,
        };
      }

      return null;
    },
    hasOwnRfcs() {
      const raw = localStorage.getItem('rfcs');
      if (!raw || raw === 'undefined') {
        return false;
      }

      try {
        const parsed = JSON.parse(raw);
        const list = Array.isArray(parsed) ? parsed : [];

        return list.some((item) => {
          const value = typeof item === 'string'
            ? item
            : (item?.rfc || item?.RFC || '');
          const rfc = String(value || '').trim().toUpperCase();
          return rfc !== '' && rfc !== this.$options.demoRfc;
        });
      } catch (_) {
        return false;
      }
    },
    isUsingDemoRfc() {
      const activeRfc = String(localStorage.getItem('rfc') || '').trim().toUpperCase();
      return activeRfc === this.$options.demoRfc;
    },
    demoBanner() {
      if (!this.isDataSectionRoute) {
        return null;
      }

      if (!this.isUsingDemoRfc || this.hasOwnRfcs) {
        return null;
      }

      return {
        kind: 'demo',
        level: 'warning',
        title: 'Estás viendo información de demostración',
        message: 'Estos datos son demo y no corresponden a tu operación real. Registra tu primer RFC para consultar tu información real en TusCFDIs.',
      };
    },
    banners() {
      const items = [];

      if (this.demoBanner) {
        items.push(this.demoBanner);
      }

      if (this.subscriptionBanner) {
        items.push({
          kind: 'subscription',
          ...this.subscriptionBanner,
        });
      }

      return items;
    },
  },
  mounted() {
    this.loadStatus();
  },
  methods: {
    async loadStatus() {
      if (this.loading) {
        return;
      }

      this.loading = true;
      try {
        const response = await accountService.getSubscription();
        this.status = response?.data?.status || null;

        const rfc = String(localStorage.getItem('rfc') || '').trim().toUpperCase();
        if (rfc) {
          const history = await dashboardService.getDownloadCenterHistory(rfc, 1, 1, { skipLoading: true });
          this.efirmaIncident = history?.efirma_incident || null;
        } else {
          this.efirmaIncident = null;
        }
      } catch (error) {
        this.status = null;
        this.efirmaIncident = null;
      } finally {
        this.loading = false;
      }
    },
    goToSubscription() {
      this.$router.push({
        name: 'settings',
        query: {
          section: 'subscription',
        },
      });
    },
    goToRfcSettings() {
      this.$router.push({
        name: 'settings',
        query: {
          section: 'rfcs',
        },
      });
    },
  },
};
</script>

<style scoped>
.global-subscription-alert {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.global-subscription-alert__content {
  min-width: 0;
}
</style>
